// @ts-nocheck
import React from "react";
import DataTable, { DataTablePresentationProps } from "@insite/mobius/DataTable";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { css } from "styled-components";
import getColor from "@insite/mobius/utilities/getColor";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import translate from "@insite/client-framework/Translate";
import { InvoicePaymentOption } from "@store/Framework/Store/Pages/AccountBalance/AccountBalanceReducer";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import useLocalizeCurrency from "@store/Framework/Common/Hooks/useLocalizeCurrency";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import { DataTableHead, DataTableHeader, DataTableRow } from "@insite/mobius/index";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import Modal, { ModalPresentationProps } from "@store/MobiusOverrides/Modal/Modal";
import parse from "html-react-parser";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import {
    useAccountBalanceCredits,
    useIsAccountBalanceLoading,
} from "@store/Framework/Common/Hooks/AccountBalance/useAccountBalanceState";

interface AccountCreditsTableStyles {
    dataTable?: DataTablePresentationProps;
    cell?: DataTableCellProps;
    labelTypographyProps?: DataTableCellProps["typographyProps"];
    cardWrapper?: InjectableCss;
    widgetWrapper?: InjectableCss;
    headerCell?: DataTableCellProps;
    tableWrapper?: InjectableCss;
    modal?: ModalPresentationProps;
}

const accountCreditsTableStyles: AccountCreditsTableStyles = {
    modal: {
        cssOverrides: {
            modalContent: css`
                padding-top: 0;
            `,
        },
    },
    tableWrapper: {
        css: css`
            ${breakpoint("sm", "down")} {
                overflow: scroll;
                max-height: 100vh;
                th {
                    position: sticky;
                    top: 0;
                }
            }
        `,
    },
    dataTable: {
        css: css`
            overflow: auto;
        `,
    },
    labelTypographyProps: {
        css: css`
            font-weight: ${({ theme }) => theme.design.typography.weight.semibold};
        `,
    },
    cell: {
        css: css`
            border: 1px solid ${getColor("common.border")};
        `,
    },
    headerCell: {
        css: css`
            border: 1px solid ${getColor("common.border")};
        `,
        typographyProps: {
            css: css`
                font-weight: ${({ theme }) => theme.design.typography.weight.semibold};
            `,
        },
    },
    cardWrapper: {
        css: css`
            box-shadow: ${({ theme }) => theme.shadows["small"]};
            display: flex;
            flex-direction: column;
            padding: ${pxToRem(20)};
            gap: ${pxToRem(10)};
            ${breakpoint("sm", "down")} {
                overflow-x: hidden;
                max-width: 100vw;
            }
        `,
    },
    widgetWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: ${pxToRem(32)};
        `,
    },
};

interface AccountBalanceCreditsModalProps {
    isOpen: boolean;
    handleClose: () => void;
    aboveContent: string;
    belowContent?: string;
}

const AccountBalanceCreditsModal: React.FC<AccountBalanceCreditsModalProps> = ({
    isOpen,
    handleClose,
    aboveContent,
    belowContent,
}) => {
    const styles = accountCreditsTableStyles;
    const isLoading = useIsAccountBalanceLoading();
    const credits = useAccountBalanceCredits();
    const currencyFormatter = useLocalizeCurrency();

    if (credits.length === 0 && !isLoading) {
        return null;
    }

    return (
        <Modal
            isOpen={isOpen}
            handleClose={handleClose}
            headline={translate("Credits Details")}
            sizeVariant="large"
            {...styles.modal}
        >
            <LoadingOverlay loading={isLoading}>
                {parse(aboveContent ?? "", parserOptions)}
                <StyledWrapper {...styles.widgetWrapper}>
                    <StyledWrapper {...styles.cardWrapper}>
                        <StyledWrapper {...styles.tableWrapper}>
                            <DataTable {...styles.dataTable}>
                                <DataTableHead>
                                    <DataTableHeader {...styles.headerCell}>{translate("Reference #")}</DataTableHeader>

                                    <DataTableHeader {...styles.headerCell}>{translate("Date")}</DataTableHeader>
                                    <DataTableHeader {...styles.headerCell}>{translate("Type")}</DataTableHeader>

                                    <DataTableHeader {...styles.headerCell}>
                                        {translate("Credit Amount")}
                                    </DataTableHeader>
                                </DataTableHead>
                                <DataTableBody>
                                    {credits.map((option: InvoicePaymentOption) => (
                                        <DataTableRow key={option.invoiceNumber}>
                                            <DataTableCell {...styles.cell}>{option.invoiceNumber}</DataTableCell>
                                            <DataTableCell {...styles.cell}>
                                                {new Date(option.date).toLocaleDateString()}
                                            </DataTableCell>
                                            <DataTableCell {...styles.cell}>{option.type}</DataTableCell>

                                            <DataTableCell {...styles.cell}>
                                                {currencyFormatter(option.balanceDue)}
                                            </DataTableCell>
                                        </DataTableRow>
                                    ))}
                                </DataTableBody>
                            </DataTable>
                        </StyledWrapper>
                    </StyledWrapper>
                    {parse(belowContent ?? "", parserOptions)}
                </StyledWrapper>
            </LoadingOverlay>
        </Modal>
    );
};

export default AccountBalanceCreditsModal;
